// stores/dashboardStore.ts
import { defineStore } from 'pinia';
import type { Workspace, Project, Model, IOItem, IOElement } from '@lambda-libs/types';
import AutoService from '@services/AutoService';
import { notify } from '@components/NotificationPlugin';
import { useAuthUserStore } from '@stores/auth'
import { sortProjectsByLastModified } from '@utils/projectSort';
// Define interface for elements object structure
interface ElementsMap {
  [key: string]: IOElement[];
}

interface ItemsMap {
  [key: string]: IOItem[];
}

let refreshWorkspacesPromise: Promise<void> | null = null;

// The workspace a project row belongs to (its `WORKSPACE#<id>` PK), or ''.
// A project lives in exactly one workspace, so this — not whatever workspace
// happens to be selected — is the only valid workspaceId to pair it with.
function workspaceIdOfProject(project: Project | null | undefined): string {
  const pk = project?.PK;
  return typeof pk === 'string' && pk.startsWith('WORKSPACE#') ? pk.slice('WORKSPACE#'.length) : '';
}

export const useDashboardStore = defineStore('dashboardStore', {
  state: () => ({
    
    selectedWorkspaceId: '' as string,
    selectedProjectId: '' as string,
   
    selectedProject: null as Project | null,
    selectedWorkspace: null as Workspace | null,
    workspaces: [] as Workspace[],
    projects: [] as Project[],
    projectStatus: null as string | null,
    // new V2
    selectedInput: null as IOItem | null,
    selectedInputElement: null as IOElement | null,
    selectedOutputElement: null as IOElement | null,
    selectedOutput: null as IOItem | null,
    inputs: {} as ItemsMap,
    inputElements: {} as ElementsMap,
    outputs: {} as ItemsMap,
    outputElements: {} as ElementsMap,
   
    selectedInputId: '' as string,
    selectedOutputId: '' as string,
    models: [] as Model[],
    selectedModel: null as Model | null,
    selectedLanguage: null as string | null,
    supportedModelLanguages: [] as string[],
    // Mode is decided by the build, not by user data. The persisted copy of this
    // store may still carry the old hard-coded `true` default, so main.js also
    // re-syncs this flag from import.meta.env.MODE on startup.
    isProductionRadio: (import.meta.env.MODE === 'productionradio') as boolean,
  }),
  
  getters: {
    // Add getter for production radio mode
    shouldUseDefaults: (state) => {
      return state.isProductionRadio;
    }
  },

  actions: {
    clearProject() {
      this.selectedInputId = '';
      this.selectedOutputId = '';
      this.selectedInputElement = null;
      this.selectedOutputElement = null;
      this.selectedInput = null;
      this.selectedOutput = null;
      this.inputs = {};
      this.outputs = {};
      this.inputElements = {};
      this.outputElements = {};
      this.projectStatus = '';
    },
    clearCache() {
      this.selectedWorkspaceId = '';
      this.selectedWorkspace = null;
      this.selectedProjectId = '';
      this.selectedProject = null;

      this.projects = [];
      this.projectStatus = '';

      this.selectedInputId = '';
      this.selectedOutputId = '';
      this.selectedInputElement = null;
      this.selectedOutputElement = null;
      this.selectedInput = null;
      this.selectedOutput = null;
      this.inputs = {};
      this.outputs = {};
      this.inputElements = {};
      this.outputElements = {};
      this.workspaces = [];
      this.projects = [];
      this.models = [];
      this.selectedModel = null;
      this.selectedLanguage = null;
      this.supportedModelLanguages = [];
    },
    setSelectedWorkspace(workspace: Workspace | null) {
      // A malformed workspace entry (missing PK) must not crash the whole
      // dashboard — treat it as "no selection" instead.
      if (workspace && !workspace.PK) {
        console.warn('setSelectedWorkspace: ignoring workspace without PK');
        workspace = null;
      }
      const nextId = workspace?.PK?.split('#')[1] ?? '';
      this.selectedWorkspace = workspace;
      this.selectedWorkspaceId = nextId;
      // Never keep a project of another workspace selected: uploads and
      // transcript orders send selectedWorkspaceId + selectedProjectId, and
      // the backend rejects a mismatched pair (inputupload 404, WebSocket
      // CreateTranscriptOrder "An error occurred").
      if (this.selectedProjectId && workspaceIdOfProject(this.selectedProject) !== nextId) {
        this.dropProjectSelection();
      }
    },
    dropProjectSelection() {
      this.selectedProject = null;
      this.selectedProjectId = '';
      this.clearProject();
    },
    // Repairs a workspace/project pair that does not belong together (e.g. a
    // persisted selection written by an older build). A selected project
    // object wins: the workspace moves to the project's own workspace. A bare
    // project id without a matching project object is dropped. Returns true
    // when the selection changed.
    reconcileSelection(): boolean {
      if (!this.selectedProjectId) return false;
      const projectWs = workspaceIdOfProject(this.selectedProject);
      const projectMatchesId = this.selectedProject?.SK === `PROJECT#${this.selectedProjectId}`;
      if (projectMatchesId && projectWs === this.selectedWorkspaceId) return false;
      if (projectMatchesId && projectWs) {
        this.selectedWorkspace = this.workspaces.find((w) => w.PK === `WORKSPACE#${projectWs}`) ?? null;
        this.selectedWorkspaceId = projectWs;
        return true;
      }
      this.dropProjectSelection();
      return true;
    },

    setSelectedInputId(id: string) {
      this.selectedInputId = id;
    },

    setSelectedOutputId(id: string) {
      this.selectedOutputId = id;
    },

    getOutputElement(outputId: string, elementId: string) {
      const response = AutoService.fetchOrder(
        `workspaces/${this.selectedWorkspaceId}/projects/${this.selectedProjectId}/inputs/${this.selectedInputId}/outputs/${outputId}/elements/${elementId}`
      );
      if (response) {
        return response;
      }
    },

    async processCompleteProjectData(workspaceId: string, projectId: string) {
      try {
        // Fetch complete project data
        const response = await AutoService.fetchOrder(
          `workspaces/${workspaceId}/projects/${projectId}/complete`
        );

        // Stale-response guard: a newer project selection supersedes this
        // fetch — never let an older reply wipe/repopulate the newer context.
        if (this.selectedWorkspaceId !== workspaceId || this.selectedProjectId !== projectId) {
          return false;
        }

        const mixedData = response.Items;
    
        if (!mixedData || !Array.isArray(mixedData)) {
          notify('danger', 'Failed to retrieve project data or invalid format');
          return false;
        }
        
        
        // Clear previous data
        this.clearProject();
        
        // Sort the mixed data into respective categories
        const inputs: IOItem[] = [];
        const outputs: IOItem[] = [];
        const inputElements: IOElement[] = [];
        const outputElements: IOElement[] = [];
        
        
        // Categorize each item in the mixed array
        mixedData.forEach((item: any) => {
          if (!item.PK || !item.SK) return; // Skip invalid items
          
          const pkParts: string[] = item.PK.split('#');
          const skParts: string[] = item.SK.split('#');
          
          if (pkParts.length < 2 || skParts.length < 2) return; // Skip invalid formats
          
          const pkType: string = pkParts[0];
          const skType: string = skParts[0];
          
          if (pkType === 'PROJECT' && skType === 'INPUT') {
            inputs.push(item as IOItem);
          } else if (pkType === 'INPUT' && skType === 'OUTPUT') {
            outputs.push(item as IOItem);
          } else if (pkType === 'INPUT' && skType === 'ELEMENT') {
            inputElements.push(item as IOElement);
          } else if (pkType === 'OUTPUT' && skType === 'ELEMENT') {
            outputElements.push(item as IOElement);
          }
        });
        
        this.projectStatus = `Inputs: ${inputs.length}, Outputs: ${outputs.length}, Input Elements: ${inputElements.length}, Output Elements: ${outputElements.length}`;
        
        // Process inputs (always recorded, even when empty, so callers can
        // tell a known-empty project apart from one never loaded).
        if (inputs.length > 0) {
          // Sort inputs by createdAt (ISO datetime descending)
          inputs.sort((a, b) => {
            if (!a.createdAt) return 1;
            if (!b.createdAt) return -1;
            return b.createdAt.localeCompare(a.createdAt);
          });
        }
        this.setInputs(projectId, inputs);
        
        // Process and group outputs by their parent input
        if (outputs.length > 0) {
          const outputsByInput: Record<string, IOItem[]> = {};
          outputs.forEach(output => {
            const inputId = output.PK.split('#')[1];
            if (!outputsByInput[inputId]) {
              outputsByInput[inputId] = [];
            }
            outputsByInput[inputId].push(output);
          });
          
          // Set outputs for each input
          Object.keys(outputsByInput).forEach(inputId => {
            this.setOutputs(inputId, outputsByInput[inputId]);
          });
        }
        
        // Process and group input elements by their parent input
        if (inputElements.length > 0) {
          const elementsByInput: Record<string, IOElement[]> = {};
          inputElements.forEach(element => {
            const inputId = element.PK.split('#')[1];
            if (!elementsByInput[inputId]) {
              elementsByInput[inputId] = [];
            }
            elementsByInput[inputId].push(element);
          });
          
          // Set input elements for each input
          Object.keys(elementsByInput).forEach(inputId => {
            this.setInputElements(inputId, elementsByInput[inputId]);
          });
        }
        
        // Process and group output elements by their parent output
        if (outputElements.length > 0) {
          const elementsByOutput: Record<string, IOElement[]> = {};
          outputElements.forEach(element => {
            const outputId = element.PK.split('#')[1];
            if (!elementsByOutput[outputId]) {
              elementsByOutput[outputId] = [];
            }
            elementsByOutput[outputId].push(element);
          });
          
          // Set output elements for each output
          Object.keys(elementsByOutput).forEach(outputId => {
            this.setOutputElements(outputId, elementsByOutput[outputId]);
          });
        }
        
        // Update UI state based on the first input if available
        if (inputs.length > 0) {
          const firstInput = inputs[0];
          this.setSelectedInput(firstInput);
          
          // If this input has elements, select the first one
          const inputId = firstInput.SK.split('#')[1];
          if (this.inputElements[inputId] && this.inputElements[inputId].length > 0) {
            this.setSelectedInputElement(this.inputElements[inputId][0]);
          }
          
          // If this input has outputs, make them available
          if (this.outputs[inputId] && this.outputs[inputId].length > 0) {
            // Don't need to refresh since we just loaded the data
            // If there are outputs, select the first one
            const firstOutput = this.outputs[inputId][0];
            this.setSelectedOutput(firstOutput);
            
            // If this output has elements, make them available
            const outputId = firstOutput.SK.split('#')[1];
            if (this.outputElements[outputId] && this.outputElements[outputId].length > 0) {
              this.setSelectedOutputElement(this.outputElements[outputId][0]);
            }
          }
        }
        
        return true;
      } catch (error: any) {
        console.error('Error processing complete project data:', error);
        notify('danger', 'Error loading project data: ' + (error.message || 'Unknown error'));
        return false;
      }
    },

    setSelectedOutput(output: IOItem | null) {
      this.selectedOutput = output;
      this.selectedOutputId = output?.SK?.split('#')[1] ?? '';
    },
   
    setSelectedInputElement(element: IOElement | null) {
      this.selectedInputElement = element;
      // PK is INPUT#<inputId>, SK is ELEMENT#<elementId> — inputId comes from PK
      this.selectedInputId = element?.PK?.split('#')[1] ?? this.selectedInputId;
      if (!this.selectedInput || this.selectedInput.SK !== element?.PK) {
        this.selectedInput = this.inputs[this.selectedProjectId]?.find((input) => input.SK === element?.PK) ?? this.selectedInput;
      }
      return true;
    },
    setSelectedOutputElement(element: IOElement | null) {
      if (this.selectedOutputElement != element) {
        this.selectedOutputElement = element;
      }
      // Keep selectedOutput/selectedOutputId in sync when an output element is chosen
      // PK is OUTPUT#<outputId>, SK is ELEMENT#<elementId>
      if (element?.PK) {
        const outputId = element.PK.split('#')[1];
        if (outputId) this.selectedOutputId = outputId;
        const parentOutput = outputId ? Object.values(this.outputs).flat().find((o) => o.SK === element.PK) : null;
        if (parentOutput) this.selectedOutput = parentOutput;
      } else if (!element) {
        // clearing selection does not clear parent output — caller decides
      }
    },
    setInputElements(elementId:string, elements: IOElement[]) {
      this.inputElements[elementId] = elements;
    },
    setOutputElements(elementId:string, elements: IOElement[]) {
      // if data is different then replace it
      if (this.outputElements[elementId] && this.outputElements[elementId].length === elements.length) {
        let isSame = true;
        for (let i = 0; i < elements.length; i++) {
          if (this.outputElements[elementId][i].SK !== elements[i].SK) {
            isSame = false;
            break;
          }
        }
        if (isSame) {
          // if there is only 1 element, select it
          if (elements.length === 1) this.setSelectedOutputElement(this.outputElements[elementId][0]);
          
          return;
        }
      }
      this.outputElements[elementId] = elements;
      // if there is only 1 element, select it
      if (elements.length === 1) this.setSelectedOutputElement(this.outputElements[elementId][0]);
      
    },
    setSelectedInput(input: IOItem | null) {
      this.selectedInput = input;
      this.selectedInputId = input?.SK?.split('#')[1] ?? '';
    },
    async setProductionRadioMode(isProductionRadio: boolean) {
     
      this.isProductionRadio = isProductionRadio;
         // Force persistence of the state change
      this.$patch({ isProductionRadio });
      if (isProductionRadio) {
        // Auto-setup default workspace and project
        this.setupDefaultWorkspaceProject();
      }
      else
      {
          try {
          const response = await AutoService.fetchOrder('workspaces');
          const refreshedWorkspaces = response.Items || [];
          const workspacesList = JSON.parse(JSON.stringify(refreshedWorkspaces));
          this.setWorkspaces(workspacesList);
        } catch (error) {
          console.error('Error fetching workspaces:', error);
        }
      }
    },
    // Single source of truth for (re)loading the workspace list: the user's own
    // workspaces plus every workspace shared with their groups, deduplicated.
    // Called on app mount, after login, and by the manual Refresh button so the
    // list is never stale. Keeps the current selection when it is still valid.
    async refreshWorkspaces(options?: { silent?: boolean }) {
      // Boot fires this concurrently from several places (shell mount, auth
      // watcher, default selection) — coalesce into one backend round-trip.
      if (refreshWorkspacesPromise) return refreshWorkspacesPromise;
      refreshWorkspacesPromise = this._doRefreshWorkspaces(options).finally(() => {
        refreshWorkspacesPromise = null;
      });
      return refreshWorkspacesPromise;
    },
    async _doRefreshWorkspaces(options?: { silent?: boolean }) {
      if (this.shouldUseDefaults) {
        await this.setupDefaultWorkspaceProject();
        return;
      }
      try {
        const authStore = useAuthUserStore();
        const userResponse = await AutoService.fetchOrder('workspaces');
        let allWorkspaces: Workspace[] = Array.isArray(userResponse.Items)
          ? userResponse.Items
          : [userResponse];

        const userGroups = authStore.userSharedGroups || [];
        if (userGroups.length > 0) {
          const groupQueries = userGroups.map(async (group: string) => {
            try {
              const groupResponse = await AutoService.fetchOrder('workspaces', undefined, undefined, {
                userGroup: group,
              });
              const groupWorkspaces = Array.isArray(groupResponse.Items)
                ? groupResponse.Items
                : [groupResponse];
              return groupWorkspaces.map((workspace: Workspace) => ({
                ...workspace,
                userGroup: group,
              }));
            } catch (error) {
              console.warn(`Failed to fetch workspaces for group ${group}:`, error);
              return [] as Workspace[];
            }
          });
          const groupWorkspaces = await Promise.all(groupQueries);
          allWorkspaces = [...allWorkspaces, ...groupWorkspaces.flat()];
          allWorkspaces = allWorkspaces.filter(
            (workspace, index, array) => array.findIndex((w) => w.PK === workspace.PK) === index
          );
        }

        const workspacesList: Workspace[] = JSON.parse(JSON.stringify(allWorkspaces));
        await this.setWorkspaces(workspacesList);

        // Drop the selection if the workspace disappeared (e.g. unshared).
        if (this.selectedWorkspaceId) {
          const stillSelected = workspacesList.find(
            (w) => w.PK === 'WORKSPACE#' + this.selectedWorkspaceId
          );
          if (!stillSelected) {
            this.setSelectedWorkspace(null);
            this.setProjects([]);
          }
        }
      } catch (error: any) {
        console.error('Error refreshing workspaces:', error);
        if (!options?.silent) {
          notify('danger', 'Error refreshing workspaces! ' + (error?.message || ''));
        }
      }
    },
    async setupDefaultWorkspaceProject() {
      try {
  
        const userId = useAuthUserStore().$state.userId;
        
        if (!userId) {
          return;
        }

        // Create mock default workspace and project for production radio mode using user ID
        const defaultWorkspace: Workspace = {
          PK: `WORKSPACE#${userId}`,
          SK: `METADATA#${userId}`,
          name: 'Default Workspace',
          userId: userId,
          createdAt: new Date().toISOString()
        };
        
        const defaultProject: Project = {
          PK: `WORKSPACE#${userId}`,
          SK: `PROJECT#${userId}`,
          name: 'Default Project',
          userId: userId,
      
          createdAt: new Date().toISOString(),
          aspectRatio: '16:9',
          projectType: 'transcript'
        };
        
        // Set the default workspace and project
        this.workspaces = [defaultWorkspace];
        this.projects = [defaultProject];
        this.setSelectedWorkspace(defaultWorkspace);
        this.setSelectedProject(defaultProject);
      } catch (error) {
        console.error('Error setting up default workspace/project:', error);
      }
    },
    // Default selection for the Transcribe entry point: when nothing is
    // selected (e.g. fresh login with an empty persisted selection), pick the
    // first workspace and its most recent project (projects are kept sorted
    // most-recently-modified-first) instead of showing a blank picker.
    // A no-op when a selection already exists or when there is nothing to
    // pick — callers navigate to the picker as before in that case.
    async ensureDefaultSelection() {
      if (this.shouldUseDefaults) {
        await this.setupDefaultWorkspaceProject();
        return;
      }
      if (this.workspaces.length === 0) {
        await this.refreshWorkspaces({ silent: true });
      }
      let workspaceFresh = false;
      if (!this.selectedWorkspace && this.workspaces.length > 0) {
        this.setSelectedWorkspace(this.workspaces[0]);
        workspaceFresh = true;
      }
      if (this.selectedWorkspace && !this.selectedProject) {
        if (workspaceFresh || this.projects.length === 0) {
          try {
            const response = await AutoService.fetchOrder(
              `workspaces/${this.selectedWorkspaceId}/projects`
            );
            const list = Array.isArray(response.Items) ? response.Items : [response];
            this.setProjects(sortProjectsByLastModified(JSON.parse(JSON.stringify(list))));
          } catch (error) {
            console.error('Error fetching default projects:', error);
          }
        }
        if (this.projects.length > 0) {
          await this.setSelectedProject(this.projects[0]);
        }
      }
    },
    async setSelectedProject(project: Project | null) {
      // Selecting a project of another workspace (a project list still showing
      // the previous workspace while the new one loads, drawer rows, deep
      // links) moves the workspace selection with it so the pair stays valid.
      const projectWs = workspaceIdOfProject(project);
      if (projectWs && projectWs !== this.selectedWorkspaceId) {
        this.selectedWorkspace = this.workspaces.find((w) => w.PK === `WORKSPACE#${projectWs}`) ?? null;
        this.selectedWorkspaceId = projectWs;
      }
      this.selectedProject = project;
      this.selectedProjectId = project?.SK?.split('#')[1] ?? '';
      
      // In production radio mode, use simplified data loading
      if (this.shouldUseDefaults) {
        // Skip complex data loading for production radio mode
        return;
      }
      
      await this.processCompleteProjectData(this.selectedWorkspaceId, this.selectedProjectId);
    },
    async setWorkspaces(workspaces: Workspace[]) {

      // In production radio mode, ignore external workspace changes
      if (this.shouldUseDefaults) {
        await this.setupDefaultWorkspaceProject();
        return;
      }
      
      this.workspaces = workspaces;
      if (this.selectedWorkspaceId && this.selectedWorkspaceId !== '') {
        const selectedWorkspace = workspaces.find((workspace) => workspace.PK === 'WORKSPACE#'+this.selectedWorkspaceId);
        if (selectedWorkspace) {
            try {
              const response = await AutoService.fetchOrder(
                `workspaces/${this.selectedWorkspaceId}/projects`
              );
              const projects = Array.isArray(response.Items) ? response.Items : [response];

              // Most recently modified first (updatedAt ?? createdAt) — the
              // DynamoDB Query returns random-UUID SK order otherwise.
              this.projects = sortProjectsByLastModified(projects);
              // if previous selecedProjectId is not in the new projects, set it to null
              if (this.selectedProjectId && this.selectedProjectId !== '') {
                const selectedProject = this.projects.find((project) => project.SK === 'PROJECT#'+this.selectedProjectId);
                if (selectedProject) {
                  await this.setSelectedProject(selectedProject);
                } else {
                  this.dropProjectSelection();
                }
              }
            } catch (error: any) {
              notify('danger', 'Error fetching projects! '+ error?.message);
            }

          return;
        }
      }

      this.projects = []; 
      this.selectedProject = null;
      this.selectedProjectId = '';
      this.clearProject();
    },
    setProjects(projects: Project[]) {
      // Most recently modified first — every project-list fetch (workspace
      // switch, post-create refresh) funnels through here.
      this.projects = sortProjectsByLastModified(projects);
      this.clearProject();
    },
 
    setInputs(projectId:string, inputs: IOItem[]) {
      this.inputs[projectId] = inputs;
      this.selectedInput = null;
      this.selectedInputElement = null;
    },
    setOutputs(inputId:string, outputs: IOItem[]) {
      this.outputs[inputId] = outputs;
      this.outputElements = {};
      this.selectedOutput = null;
    },
    setModels(models: Model[]) {
      this.models = models;
    },
    setSelectedModel(model: Model | null) {
      this.selectedModel = model;
    },
    setSelectedLanguage(language: string | null) {
      this.selectedLanguage = language;
    },
    setSupportedModelLanguages(languages: string[]) {
      this.supportedModelLanguages = languages;
      this.selectedLanguage = null;
    },
    // Add other actions as needed
  },
  persist: {
    // Heal selections persisted before the pair was kept consistent.
    afterRestore: ({ store }) => {
      (store as unknown as { reconcileSelection: () => boolean }).reconcileSelection();
    },
  },
});
